import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/i18n";
import Navbar from "@/components/Navbar";
import InstallPWA from "@/components/InstallPWA";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import {
  BookOpen,
  Trophy,
  Target,
  Clock,
  ArrowRight,
  Landmark,
  MapPin,
  Scale,
  Building,
  Flag,
  Users,
  TrendingUp,
  CheckCircle,
  Play,
  Globe,
  Shield,
  Palette,
  FileText,
  Heart,
} from "lucide-react";

const categoryIcons = {
  histoire: Landmark,
  geographie: MapPin,
  valeurs: Scale,
  institutions: Building,
  symboles: Flag,
  culture: Palette,
  europe: Globe,
  droits: Shield,
  societe: Users,
  demarches: FileText
};

const categoryColors = {
  histoire: "bg-amber-500",
  geographie: "bg-emerald-500",
  valeurs: "bg-blue-500",
  institutions: "bg-purple-500",
  symboles: "bg-red-500",
  culture: "bg-pink-500",
  europe: "bg-indigo-500",
  droits: "bg-cyan-500",
  societe: "bg-orange-500",
  demarches: "bg-teal-500"
};

const Dashboard = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [progress, setProgress] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        const [categoriesRes, progressRes, historyRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/progress`, { headers }),
          axios.get(`${API}/quiz-history?limit=5`, { headers })
        ]);
        
        setCategories(categoriesRes.data);
        setProgress(progressRes.data);
        setHistory(historyRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  const getCategoryProgress = (categoryId) => {
    if (!progress?.categories_progress?.[categoryId]) return 0;
    const cat = progress.categories_progress[categoryId];
    return cat.completed > 0 ? Math.round((cat.correct / cat.completed) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-2">
            {t("dash_hello")}, {user?.name} !
          </h1>
          <p className="text-slate-600">
            {t("dash_continue")}
          </p>
        </div>

        {/* PWA Install Banner */}
        <div className="mb-6">
          <InstallPWA />
        </div>

        {/* Admin Banner */}
        {user?.role === "admin" && (
          <div
            className="mb-6 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-xl p-4 flex items-center justify-between gap-4 cursor-pointer hover:from-blue-800 hover:to-blue-700 transition-all"
            onClick={() => navigate("/admin")}
            data-testid="admin-banner"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-sm">Espace Administrateur</p>
                <p className="text-xs text-blue-200">Gérer les utilisateurs, voir les donations et statistiques</p>
              </div>
            </div>
            <Button size="sm" className="bg-white text-blue-900 hover:bg-blue-50 shrink-0" data-testid="admin-banner-btn">
              Accéder
            </Button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <Card className="stat-card">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-blue-900" />
              </div>
              <div>
                <p className="stat-value">{progress?.total_quizzes || 0}</p>
                <p className="stat-label">{t("dash_quizzes_done")}</p>
              </div>
            </div>
          </Card>
          
          <Card className="stat-card">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Target className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="stat-value">{progress?.success_rate || 0}%</p>
                <p className="stat-label">{t("dash_success_rate")}</p>
              </div>
            </div>
          </Card>
          
          <Card className="stat-card">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="stat-value">{progress?.correct_answers || 0}</p>
                <p className="stat-label">{t("dash_correct")}</p>
              </div>
            </div>
          </Card>
          
          <Card className="stat-card">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Trophy className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="stat-value">{progress?.total_questions || 0}</p>
                <p className="stat-label">{t("dash_questions_tried")}</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Categories Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif font-bold text-slate-900">
                Catégories de quiz
              </h2>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {categories.map((category) => {
                const Icon = categoryIcons[category.id] || BookOpen;
                const colorClass = categoryColors[category.id] || "bg-slate-500";
                const categoryProgress = getCategoryProgress(category.id);
                
                return (
                  <Card 
                    key={category.id}
                    className="card-hover cursor-pointer group"
                    onClick={() => navigate(`/quiz/${category.id}`)}
                    data-testid={`category-${category.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`h-12 w-12 ${colorClass} rounded-xl flex items-center justify-center`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-blue-900 group-hover:translate-x-1 transition-all" />
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-1">{category.name}</h3>
                      <p className="text-sm text-slate-500 mb-4">{category.description}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Progression</span>
                          <span className="font-medium text-slate-700">{categoryProgress}%</span>
                        </div>
                        <Progress value={categoryProgress} className="h-2" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Exam CTA */}
            <Card className="mt-6 bg-gradient-to-r from-blue-900 to-blue-800 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-serif font-bold mb-2">Examen blanc</h3>
                    <p className="text-blue-100">
                      Testez-vous en conditions réelles avec 20 questions chronométrées.
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate("/exam")}
                    className="bg-white text-blue-900 hover:bg-blue-50 shrink-0"
                    data-testid="start-exam-btn"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Commencer
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-serif">Activité récente</CardTitle>
              </CardHeader>
              <CardContent>
                {history.length > 0 ? (
                  <div className="space-y-4">
                    {history.map((item, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                      >
                        <div>
                          <p className="font-medium text-slate-900 capitalize">{item.category}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(item.completed_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <div className={`text-sm font-medium ${item.percentage >= 70 ? 'text-green-600' : item.percentage >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                          {item.score}/{item.total} ({item.percentage}%)
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">
                    Aucune activité pour le moment
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-serif">{t("dash_quick")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate("/fiches")}
                  data-testid="quick-fiches-btn"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Fiches de révision
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate("/exam")}
                  data-testid="quick-exam-btn"
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Examen blanc
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  onClick={() => navigate("/donation")}
                  data-testid="quick-donation-btn"
                >
                  <Heart className="h-4 w-4 mr-2" />
                  Faire un don
                </Button>
              </CardContent>
            </Card>

            {/* Tips Card */}
            <Card className="bg-blue-50 border-blue-100">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 bg-blue-900 rounded-full flex items-center justify-center shrink-0">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Conseil du jour</h4>
                    <p className="text-sm text-slate-600">
                      Révisez régulièrement les fiches sur les valeurs de la République. 
                      C'est un thème central du test civique.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
